import React, { useContext, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import Context from '../../../utils/context';
import { isPhoneCallsEnabled } from '../../../helpers/feature_checker.js';
import { PhosphorIcon, PHOSPHOR_PATHS } from '../../users/conversation_room/phosphor_icons';
import shellStyles from '../../users/conversation_room/unified_layout.module.css';
import styles from '../new_settings.module.css';
import { SidebarChevronDown, displayNameFor, initialsFor } from './icons';

export default function SettingsShell({ children, mobileTitle = 'Settings' }) {
  const context = useContext(Context);
  const user = context.auth.getCurrentUser();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [sidebarAvatarFailed, setSidebarAvatarFailed] = useState(false);
  const profileMenuRef = useRef(null);

  const isMentor = user?.current_profile_type === 'PatientPartner';
  const phoneCallsEnabled = isMentor && isPhoneCallsEnabled(context.auth.currentAccount());
  const userDisplayName = displayNameFor(user);
  const userInitials = initialsFor(userDisplayName);
  const userAvatarUrl = user?.profile?.photo || user?.current_profile?.photo || '';
  const unreadCount = user?.current_profile?.unread_notification_count || 0;
  const handleLogout = () => {
    context.auth.userLogOut();
  };

  return (
    <div className={shellStyles.conversation_room_container}>
      <div className={shellStyles.conversation_room_wrapper}>
        <div className={styles.desktop_panels}>
          <aside className={shellStyles.app_sidebar} aria-label='Messages navigation'>
            <Link to='/member/conversations' className={shellStyles.app_sidebar_logo}>
              <img src='/wordmark.svg' alt='PatientPartner' draggable='false' />
            </Link>

            <nav className={shellStyles.app_sidebar_nav}>
              <p className={shellStyles.app_sidebar_eyebrow}>Menu</p>
              <Link to='/member/conversations' className={shellStyles.app_sidebar_item}>
                <span className={shellStyles.app_sidebar_icon}>
                  <PhosphorIcon path={PHOSPHOR_PATHS.chatCircle.regular} size={18} />
                  {unreadCount > 0 && <i />}
                </span>
                <span>Messages</span>
              </Link>
              {phoneCallsEnabled && (
                <Link to='/member/calls' className={shellStyles.app_sidebar_item}>
                  <span className={shellStyles.app_sidebar_icon}>
                    <PhosphorIcon path={PHOSPHOR_PATHS.phone.regular} size={18} />
                  </span>
                  <span>Calls</span>
                  <small>New</small>
                </Link>
              )}
            </nav>

            <div className={shellStyles.app_sidebar_footer}>
              <div className={shellStyles.app_sidebar_profile_area} ref={profileMenuRef}>
                {profileMenuOpen && (
                  <div className={shellStyles.app_sidebar_profile_menu}>
                    <Link to='/member/settings' onClick={() => setProfileMenuOpen(false)}>
                      <PhosphorIcon path={PHOSPHOR_PATHS.gear.regular} size={16} />
                      <span>Settings</span>
                    </Link>
                    <button type='button' onClick={handleLogout}>
                      <PhosphorIcon path={PHOSPHOR_PATHS.signOut.regular} size={16} />
                      <span>Sign out</span>
                    </button>
                  </div>
                )}
                <button
                  type='button'
                  className={`${shellStyles.app_sidebar_profile} ${
                    profileMenuOpen ? shellStyles.app_sidebar_profile_open : ''
                  }`}
                  onClick={() => setProfileMenuOpen((open) => !open)}
                  aria-expanded={profileMenuOpen}
                  aria-haspopup='menu'
                >
                  <span className={shellStyles.app_sidebar_avatar}>
                    {userAvatarUrl && !sidebarAvatarFailed ? (
                      <img src={userAvatarUrl} alt='' draggable='false' onError={() => setSidebarAvatarFailed(true)} />
                    ) : (
                      userInitials || 'PP'
                    )}
                  </span>
                  <span>
                    <strong>{userDisplayName}</strong>
                    <small>{isMentor ? 'Mentor' : 'Mentee'}</small>
                  </span>
                  <SidebarChevronDown className={shellStyles.app_sidebar_profile_caret} />
                </button>
              </div>
            </div>
          </aside>

          <main className={styles.settings_panel}>
            <header className={styles.mobile_topbar}>
              <Link to='/member/conversations' className={styles.mobile_logo} aria-label='PatientPartner conversations'>
                <img src='/wordmark.svg' alt='PatientPartner' draggable='false' />
              </Link>
              <span>{mobileTitle}</span>
            </header>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
